import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'

export default function CsatPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const prefillRating = searchParams.get('rating') ? Number(searchParams.get('rating')) : null

  const [selectedRating, setSelectedRating] = useState<number | null>(prefillRating)
  const [hoveredRating, setHoveredRating] = useState<number | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [alreadyRated, setAlreadyRated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (!selectedRating || !token) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.post(`/support/csat/${token}`, { rating: selectedRating })
      if (res.data?.message?.includes('already')) {
        setAlreadyRated(true)
      }
      setSubmitted(true)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      if (msg?.includes('already')) {
        setAlreadyRated(true)
        setSubmitted(true)
      } else {
        setError(msg ?? 'Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const displayRating = hoveredRating ?? selectedRating

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50" p={4}>
      <Card maxW="480px" w="100%" shadow="lg">
        <CardBody p={8}>
          {submitted ? (
            <VStack spacing={4} textAlign="center">
              <Text fontSize="4xl">{alreadyRated ? '📋' : '🎉'}</Text>
              <Heading size="md">
                {alreadyRated ? 'Already submitted' : 'Thank you for your feedback!'}
              </Heading>
              <Text color="gray.600">
                {alreadyRated
                  ? 'You have already submitted a rating for this ticket.'
                  : 'Your rating has been recorded. We appreciate you taking the time to share your experience.'}
              </Text>
            </VStack>
          ) : (
            <VStack spacing={6} align="stretch">
              <VStack spacing={1} textAlign="center">
                <Heading size="md">How did we do?</Heading>
                <Text color="gray.500" fontSize="sm">
                  Please rate your support experience
                </Text>
              </VStack>

              <VStack spacing={2}>
                <HStack spacing={2} justify="center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Box
                      key={star}
                      as="button"
                      fontSize="3xl"
                      cursor="pointer"
                      transition="transform 0.1s"
                      transform={displayRating && star <= displayRating ? 'scale(1.15)' : 'scale(1)'}
                      color={displayRating && star <= displayRating ? 'yellow.400' : 'gray.300'}
                      onClick={() => setSelectedRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(null)}
                      aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    >
                      ★
                    </Box>
                  ))}
                </HStack>
                <Text fontSize="sm" color="gray.500" h="20px">
                  {displayRating === 1 && 'Very dissatisfied'}
                  {displayRating === 2 && 'Dissatisfied'}
                  {displayRating === 3 && 'Neutral'}
                  {displayRating === 4 && 'Satisfied'}
                  {displayRating === 5 && 'Very satisfied'}
                </Text>
              </VStack>

              {error && (
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {error}
                </Text>
              )}

              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={loading}
                isDisabled={!selectedRating}
                size="lg"
                w="100%"
              >
                Submit Rating
              </Button>
            </VStack>
          )}
        </CardBody>
      </Card>
    </Box>
  )
}
